import { Link } from "react-router-dom";
import { ArrowRight, AlertTriangle, TrendingUp, ListChecks, Sparkles, Compass } from "lucide-react";
import { useDashboard } from "@/components/layout/AppShell";
import { ScoreRing } from "@/components/ScoreRing";
import { OrbitMap } from "@/components/OrbitMap";
import { SourceBadge } from "@/components/SourceBadge";
import { EmptyState } from "@/components/PageState";
import { PRIORITY_META, LEVEL_META } from "@/lib/orbit";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function ScoreCard({ label, score, level, valueLabel, verify, to }) {
  const meta = LEVEL_META[level] || {};
  return (
    <Link to={to} className="group rounded-2xl border border-border bg-card p-6 transition-colors duration-200 hover:border-accent/40" data-testid={`score-card-${label}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-muted-foreground">{label}</p>
          <p className="mt-1 font-display text-lg font-semibold" style={{ color: meta.color }}>{level}</p>
          {valueLabel && <p className="mt-1 text-xs text-muted-foreground">{valueLabel}</p>}
        </div>
        <ScoreRing score={score} level={level} size={92} stroke={8} />
      </div>
      <div className="mt-4 flex items-center justify-between">
        <SourceBadge type={verify ? "verify" : "calculated"} />
        <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-hover:translate-x-1" />
      </div>
    </Link>
  );
}

export default function Dashboard() {
  const { data, loading } = useDashboard();

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-72" />
        <div className="grid gap-6 md:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-40 rounded-2xl" />)}</div>
        <Skeleton className="h-96 rounded-2xl" />
      </div>
    );
  }

  const name = data?.organisation_name || "Your Business";

  if (!data?.has_data) {
    return (
      <div>
        <div className="mb-8">
          <div className="orbit-eyebrow mb-2">Executive Dashboard</div>
          <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{greeting()}, {name}</h1>
        </div>
        <EmptyState
          title="Your dashboard unlocks after your assessment"
          whatsMissing="You haven't completed the ORBIT assessment yet, so there are no scores or opportunities to show."
          whyItMatters="The assessment is what lets ORBIT map your business and calculate your Digital Maturity and AI Readiness deterministically."
          action="Begin the assessment"
        />
        <div className="mt-6 rounded-2xl border border-dashed border-border bg-card/50 p-6 text-center">
          <p className="text-sm text-muted-foreground">Want to see the full experience first?</p>
          <Button variant="outline" asChild className="mt-3"><Link to="/app/business-map">Preview the Business Map</Link></Button>
        </div>
      </div>
    );
  }

  const s = data.scores;
  const topOpp = data.opportunities?.[0];
  const attention = data.domains?.filter((d) => d.status === "At Risk").slice(0, 3) || [];

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="orbit-eyebrow mb-2">Executive Dashboard</div>
          <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{greeting()}, {name}</h1>
          <p className="mt-1 text-sm text-muted-foreground">Here is where your business stands and what should happen next.</p>
        </div>
        <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90"><Link to="/app/reports" data-testid="dashboard-report-btn">View full report <ArrowRight className="h-4 w-4" /></Link></Button>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <ScoreCard label={s.digital_maturity.label} score={s.digital_maturity.score} level={s.digital_maturity.level} to="/app/maturity" />
        <ScoreCard label={s.ai_readiness.label} score={s.ai_readiness.score} level={s.ai_readiness.level} to="/app/ai-readiness" />
        <ScoreCard label={s.transformation_potential.label} score={s.transformation_potential.score} level={s.transformation_potential.level} valueLabel={s.transformation_potential.value_label} verify={s.transformation_potential.verify} to="/app/opportunities" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        <div className="rounded-2xl border border-border bg-card p-6 lg:col-span-3">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="font-display text-lg font-semibold">Your Business Map</h2>
              <p className="text-sm text-muted-foreground">Ten domains, one system. Hover a node for detail.</p>
            </div>
            <SourceBadge type="responses" />
          </div>
          <div className="flex justify-center">
            <OrbitMap domains={data.domains} businessName={name} size={440} />
          </div>
        </div>

        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="mb-3 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <h3 className="font-display text-base font-semibold">What needs attention</h3>
            </div>
            <ul className="space-y-2.5">
              {attention.length ? attention.map((d) => (
                <li key={d.key} className="flex items-center justify-between gap-3 text-sm">
                  <span className="font-medium">{d.label}</span>
                  <span className="font-num text-xs text-red-600">{d.score} · {d.status}</span>
                </li>
              )) : <li className="text-sm text-muted-foreground">No areas are currently at risk.</li>}
            </ul>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="mb-3 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-emerald-600" />
              <h3 className="font-display text-base font-semibold">Where value is available</h3>
            </div>
            <p className="text-sm text-muted-foreground">{data.opportunities?.length || 0} prioritised opportunities identified across your business.</p>
            <Button variant="outline" size="sm" asChild className="mt-3"><Link to="/app/opportunities">Open Opportunity Radar</Link></Button>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="mb-3 flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-accent" />
              <h3 className="font-display text-base font-semibold">Roadmap progress</h3>
            </div>
            <div className="space-y-2.5">
              {data.roadmap?.map((p) => (
                <div key={p.phase} className="flex items-center justify-between text-sm" data-testid={`dash-roadmap-${p.phase}`}>
                  <span className="text-muted-foreground">{p.subtitle} · {p.title}</span>
                  <span className="font-num text-xs font-semibold">{p.items.length} action{p.items.length === 1 ? "" : "s"}</span>
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" asChild className="mt-4"><Link to="/app/roadmap">View 90-day roadmap</Link></Button>
          </div>
        </div>
      </div>

      {topOpp && (
        <div className="rounded-2xl border border-border bg-gradient-to-br from-card to-secondary/40 p-6 sm:p-8">
          <div className="flex items-center gap-2">
            <ListChecks className="h-4 w-4 text-accent" />
            <span className="orbit-eyebrow">What should happen next</span>
          </div>
          <div className="mt-4 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-display text-xl font-semibold">{topOpp.title}</h3>
                <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${PRIORITY_META[topOpp.priority]?.bg} ${PRIORITY_META[topOpp.priority]?.text} ${PRIORITY_META[topOpp.priority]?.border}`}>{topOpp.priority}</span>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{topOpp.problem}</p>
              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                <span><span className="text-muted-foreground">Impact: </span><span className="font-semibold">{topOpp.impact}</span></span>
                <span><span className="text-muted-foreground">Effort: </span><span className="font-semibold">{topOpp.effort}</span></span>
                <span><span className="text-muted-foreground">Readiness: </span><span className="font-semibold">{topOpp.readiness}</span></span>
              </div>
            </div>
            <Button asChild className="shrink-0"><Link to="/app/opportunities">See recommended step <ArrowRight className="h-4 w-4" /></Link></Button>
          </div>
        </div>
      )}
    </div>
  );
}
